/**
 * Catalan strings, the product's first language. Keys are stable identifiers;
 * a second language is another file with the same keys.
 */
import { caAreas } from './ca-imports';

export const ca = {
  ...caAreas,
  loading: 'Carregant…',
  platformDown: 'La plataforma no respon.',
  noPanel: 'Cap panell connectat: les pàgines de servidors mostraran aquest estat.',
  version: (v: string) => `Plataforma de missions · versió ${v}`,
  signIn: 'Entra',
  signOut: 'Surt',
  register: 'Crea el compte',
  registerFirst: 'Crea el primer compte',
  handle: 'Nom d’usuari',
  handleHint: 'Minúscules, xifres, punt, guió o guió baix; de 3 a 32 caràcters.',
  displayName: 'Nom que veuran els altres',
  password: 'Contrasenya',
  passwordHint: 'Deu caràcters com a mínim. Una frase és més fàcil de recordar que un garbuix.',
  remember: 'Recorda’m en aquest dispositiu',
  inviteCode: 'Codi d’invitació',
  inviteHint: 'Te l’ha de donar qui administra la plataforma.',
  inviteValid: 'Invitació vàlida',
  inviteChecking: 'Comprovant la invitació…',
  inviteInvalid:
    'Aquesta invitació no és vàlida o ha caducat. Demana’n una de nova a un administrador, o escriu un altre codi.',
  inviteUnchecked:
    'No hem pogut comprovar la invitació ara mateix; la comprovarem quan creïs el compte.',
  forgotPassword:
    'No recordes la contrasenya? Entra amb Steam o Discord si els tens enllaçats, o demana-la a un administrador.',
  steamJoinTitle: 'Ja has jugat amb nosaltres',
  steamJoinIntro: (who: string | null) =>
    who
      ? `Steam ens diu que ets ${who}, i el servidor et té registrat de partides anteriors. Tria un nom d’usuari i obre el teu compte ara mateix: no et cal cap codi d’invitació.`
      : 'Steam t’ha identificat i el servidor et té registrat de partides anteriors. Tria un nom d’usuari i obre el teu compte ara mateix: no et cal cap codi d’invitació.',
  steamJoinHaveAccount:
    'Si ja hi tenies compte, entra-hi amb la contrasenya i Steam hi quedarà enllaçat.',
  discordJoinTitle: 'Ja ets al nostre Discord',
  discordJoinIntro: (who: string | null) =>
    who
      ? `Discord ens diu que ets ${who}, i ets al servidor de Discord del grup. Tria un nom d’usuari i obre el teu compte ara mateix: no et cal cap codi d’invitació.`
      : 'Discord t’ha identificat i ets al servidor de Discord del grup. Tria un nom d’usuari i obre el teu compte ara mateix: no et cal cap codi d’invitació.',
  discordJoinHaveAccount:
    'Si ja hi tenies compte, entra-hi amb la contrasenya i Discord hi quedarà enllaçat.',
  joinPassword: 'Contrasenya (opcional)',
  joinPasswordHint:
    'Si en poses una, també podràs entrar amb usuari i contrasenya. Deu caràcters com a mínim; la pots afegir més tard des del teu perfil.',
  membershipUnsure:
    'Ara mateix Discord no ens ha dit si ets al servidor del grup. Torna a prémer «Entra amb Discord» d’aquí a una estona: si hi ets, podràs crear el compte sense invitació.',
  haveAccount: 'Ja tens compte?',
  noAccount: 'Encara no tens compte?',
  firstAccountNote: 'Encara no hi ha cap compte. El primer que es creï serà el d’administració.',
  signedInAs: (name: string) => `Has entrat com a ${name}`,
  roles: 'Rols',
  identities: 'Maneres d’entrar',
  steamSignIn: 'Entra amb Steam',
  steamLink: 'Enllaça Steam',
  steamUnlink: 'Desenllaça Steam',
  steamLinkHint:
    'Amb Steam enllaçat, les estadístiques de campanya que el servidor ha registrat amb el teu identificador apareixen al teu perfil, i pots entrar sense contrasenya.',
  orSignInWith: 'O bé',
  discordSignIn: 'Entra amb Discord',
  landingMissions: 'Missions',
  landingReports: 'Informes',
  landingPanel: 'Panell de servidors',
  landingProfile: 'El meu perfil',
  landingAdmin: 'Administració',
  landingSignedIn: (name: string) => `Sessió oberta com a ${name}`,
  reports: 'Informes de partida',
  reportsIntro:
    'Totes les partides que el servidor ha registrat, amb l’informe públic de cada una. La llista es refresca sola cada deu minuts des del panell.',
  reportsEmpty: 'Encara no hi ha cap informe.',
  reportOpen: 'Informe',
  reportPdf: 'PDF',
  reportDelete: 'Elimina',
  reportDeleteConfirm: {
    title: 'Eliminar aquest informe?',
    body: 'S’elimina del panell. No es pot desfer.',
  },
  reportRegenerate: 'Regenera',
  reportsSync: 'Refresca des del panell',
  reportsSynced: (n: number, r: number) => `${n} partides, ${r} llistes de jugadors noves.`,
  reportsLastSync: (when: string) => `Última sincronització: ${when}`,
  /**
   * What the panel calls a match: LIVE, CLOSED and DISCARDED are the values the
   * platform's own code reads or writes (`PanelMatch`), ENDED is the one the
   * reports page was seen showing raw (the owner, 2026-10-14). A value not here
   * is never printed alone: `reportStatusUnknown` plus the value in small print
   * (`app/reportStatus.ts`).
   */
  reportStatus: {
    LIVE: 'en curs',
    CLOSED: 'tancada',
    ENDED: 'acabada',
    DISCARDED: 'descartada',
  } as Record<string, string>,
  reportStatusUnknown: 'estat desconegut',
  reportPlayers: (n: number) => (n === 1 ? '1 jugador' : `${n} jugadors`),
  reportKills: (n: number, f: number) => `${n} baixes${f ? ` (${f} fratricides)` : ''}`,
  reportDuration: (s: number) => `${Math.floor(s / 3600)} h ${Math.floor((s % 3600) / 60)} min`,
  profileMatches: 'Partides jugades',
  profileMatchesHint:
    'Les partides del servidor en què el teu identificador de Steam era a la llista de jugadors. Enllaça Steam perquè hi surtin.',
  signInIntro:
    'Entra amb el teu usuari i contrasenya. Si al teu perfil has verificat Steam o Discord, també pots entrar amb un clic.',
  passwordSignIn: 'Entra amb usuari i contrasenya',
  signInDoorsNote:
    'És el mateix compte entris per on entris. La primera vegada amb Steam o Discord et demanarem un sol cop l’usuari i la contrasenya, per lligar-los; les altres portes les pots afegir després.',
  pendingNotice: (provider: string, who: string | null) =>
    `${provider} ha confirmat qui ets${who ? ` («${who}»)` : ''}, però cap compte de SALC té aquest ${provider} verificat. Entra ara amb el teu usuari i contrasenya, aquí sota, i quedarà verificat i enllaçat al teu compte. Si encara no tens compte, per crear-lo et cal una invitació: demana-la a un administrador i, quan el creïs, passarà el mateix.`,
  linkedNowNotice: (provider: string) => `${provider} verificat i enllaçat al teu compte.`,
  claimFallback: 'No ho pots verificar ara? Escriu-lo i queda com a declarat',
  claimNoDoor:
    'Declarat: suma les partides al perfil, però no serveix per entrar fins que ho verifiquis.',
  identitiesTitle: 'Els teus comptes de joc i d’entrada',
  howItWorksLabel: 'Com funciona',
  identitiesIntro: 'SALC et pot reconèixer per tres portes: usuari i contrasenya, Steam i Discord.',
  identitiesIntroMore:
    'Steam és la que compta per al joc: el servidor identifica cada jugador pel seu SteamID64, i si l’enllaces, les partides que has jugat (assistència, rangs, medalles) se sumen al teu perfil.',
  identityVerified: 'verificat',
  identityVerifiedMark: 'verificat ✓',
  identityClaimed: 'declarat, sense verificar',
  identityNone: 'sense enllaçar',
  identitySeenAs: (name: string) => `vist en partida com a «${name}»`,
  steamHow: 'Verifica-ho amb Steam: un clic i és la manera segura d’enllaçar-lo.',
  steamHowMore:
    'És l’única que permet entrar amb Steam. També pots escriure el teu SteamID64 o l’adreça del teu perfil de Steam: queda com a declarat fins que ho verifiquis, i un administrador també ho pot enllaçar per tu.',
  steamIdLabel: 'SteamID64 o adreça del perfil de Steam',
  steamIdHint:
    'Disset xifres, com 76561198012345678. La trobes a Steam: el teu perfil › Edita el perfil (l’adreça acaba en /profiles/<xifres>), o a Detalls del compte.',
  steamVerify: 'Verifica amb Steam',
  steamVerifyHint:
    'T’envia a iniciar sessió a Steam i torna aquí; SALC només rep el teu identificador.',
  discordHow:
    'Discord serveix per entrar amb un clic i perquè la plataforma sàpiga qui ets als avisos.',
  discordHowMore:
    'Verifica-ho amb Discord si aquesta instal·lació ho té activat, o escriu el teu nom d’usuari de Discord.',
  discordNameLabel: 'Nom d’usuari de Discord',
  discordNameHint:
    'El nom d’usuari (no el nom que es mostra), sense @, tal com surt a Discord › Configuració d’usuari › El meu compte.',
  discordVerify: 'Verifica amb Discord',
  claimSave: 'Desa',
  claimRemove: 'Treu',
  doorClosedHint:
    'Aquesta instal·lació no té activada la verificació; el que escriguis queda com a declarat.',
  welcomeTitle: (name: string) => `Benvingut/da, ${name}`,
  welcomeIntro:
    'Abans de començar, dues preguntes que ens ajuden a reconèixer-te a les partides. Pots respondre-les ara o més tard des del teu perfil; no les tornarem a fer.',
  welcomeContinue: 'Continua',
  welcomeSkip: 'Ara no, continua',
  welcomeCan: 'Què hi pots fer',
  welcomeCanSessions: 'Mira què es juga i entra-hi amb un clic',
  welcomeCanCreate: 'Crea una missió nova, pas a pas',
  welcomeCanTests: 'Fes proves guiades i explica què hi has vist',
  welcomeSteamRecommended: 'Recomanat: enllaça Steam, que és com el joc sap qui ets.',
  signInDoors: 'Portes d’entrada',
  signInDoorsIntro:
    'Aquí decideixes com pot entrar la gent a més de l’usuari i contrasenya. Els canvis s’apliquen a l’instant, sense reiniciar, i valen per a tothom.',
  steamDoor: 'Entrada i verificació amb Steam',
  steamDoorHint:
    'No cal cap configuració a Steam: només que l’adreça pública de la plataforma sigui la que la gent fa servir al navegador.',
  steamPlayersJoin: 'Els jugadors de sempre entren amb Steam',
  steamPlayersJoinHint:
    'Qui ja ha jugat als nostres servidors entra amb Steam i té compte a l’instant, sense codi ni contrasenya: Steam confirma qui és i el servidor ja el coneix. Si ho desactives, aquesta gent veurà la pantalla de contrasenya i necessitarà una invitació.',
  discordDoor: 'Entrada i verificació amb Discord',
  discordDoorHint:
    'Cal una aplicació de Discord: a discord.com/developers/applications crea’n una, a OAuth2 afegeix-hi la redirecció de sota, i copia aquí l’identificador i el secret del client.',
  discordRedirect: (url: string) => `Redirecció a registrar a Discord: ${url}`,
  discordClientId: 'Identificador del client (Client ID)',
  discordClientSecret: 'Secret del client (Client Secret)',
  discordSecretSet: 'Hi ha un secret desat; deixa-ho en blanc per mantenir-lo.',
  discordSecretUnset: 'Cap secret desat.',
  discordNotConfiguredWarning:
    'Discord està activat però sense aplicació: la porta queda tancada fins que hi posis l’identificador i el secret.',
  saveSettings: 'Desa la configuració',
  settingsSaved: 'Configuració desada.',
  gamePlayers: 'Jugadors vistos en partida',
  gamePlayersIntro:
    'Cada cop que el servidor envia una instantània o es tanca un capítol, SALC apunta els jugadors pel seu SteamID64. Enllaça’ls a un compte perquè les seves partides surtin al perfil: queda com a declarat fins que la persona ho verifiqui amb Steam des del seu perfil.',
  noPlayersYet:
    'Encara no s’ha vist cap jugador: cal jugar una sessió creada des de la plataforma.',
  linkToAccount: 'Enllaça a…',
  linkedTo: (handle: string) => `enllaçat a @${handle}`,
  unlinkPlayer: 'Desenllaça',
  seenTimes: (n: number) => (n === 1 ? '1 vegada' : `${n} vegades`),
  adminIdentities: 'Comptes enllaçats',
  adminIdentitiesHint:
    'Escriu el SteamID64 (o l’adreça del perfil) i el nom de Discord d’aquesta persona; queden com a declarats fins que ella els verifiqui.',
  discordLink: 'Enllaça Discord',
  discordUnlink: 'Desenllaça Discord',
  discordLinkHint: 'Amb Discord enllaçat pots entrar sense contrasenya.',
  discordNotices: {
    unknown:
      'Aquest compte de Discord no està enllaçat a cap compte. Entra amb usuari i contrasenya i enllaça’l des del teu perfil.',
    failed: 'Discord no ha confirmat la identitat. Torna-ho a provar.',
    linked: 'Discord enllaçat.',
    taken: 'Aquest compte de Discord ja està enllaçat a un altre compte.',
  } as Record<string, string>,
  steamNotices: {
    unknown:
      'Aquest compte de Steam no està enllaçat a cap compte. Entra amb usuari i contrasenya i enllaça’l des del teu perfil.',
    failed: 'Steam no ha confirmat la identitat. Torna-ho a provar.',
    linked: 'Steam enllaçat.',
    taken: 'Aquest compte de Steam ja està enllaçat a un altre compte.',
  } as Record<string, string>,
  providers: { STEAM: 'Steam', DISCORD: 'Discord', PASSWORD: 'Usuari i contrasenya' } as const,
  roleNames: {
    VIEWER: 'Convidat',
    PLAYER: 'Jugador',
    EDITOR: 'Creador de missions',
    COMMANDER: 'Comandant',
    CURATOR: 'Director de partida',
    SERVER_MANAGER: 'Gestor de servidors',
    PANEL_ACCESS: 'Accés al panell',
    TESTER: 'Provador',
    ADMIN: 'Administració',
  } as const,
  roleDescriptions: {
    VIEWER: 'Qui obre un enllaç sense compte: veu la pàgina de sessió i el briefing.',
    PLAYER: 'Tot compte: vota, proposa, llegeix el briefing, té perfil.',
    EDITOR: 'Genera i edita missions i campanyes, i crea sessions per jugar-les.',
    COMMANDER:
      'Triat per a una sola sessió, per la direcció o per votació: obre i tanca les votacions, accepta o rebutja les propostes i pot passar el comandament a un altre jugador. No es concedeix aquí.',
    CURATOR: 'Dirigeix partides en viu: pàgina de direcció, ordres al servidor, votacions.',
    SERVER_MANAGER: 'Crea, arrenca i atura servidors des de SALC i llança escaneigs del catàleg.',
    PANEL_ACCESS: 'Pot entrar al panell de servidors (ASM) amb el compte de SALC.',
    TESTER:
      'Llegeix a «Proves» el que ja està revisat i el que no cal revisar, totes les gires i el resum; no hi pot canviar res.',
    ADMIN: 'Tot: comptes, rols, portes d’entrada, integracions.',
  } as Record<string, string>,
  rolesLegend: 'Què permet cada rol',
  servers: 'Servidors',
  serversIntro:
    'Els servidors del panell. Crear-ne, el pressupost i la guarda de propietat arriben amb el paquet 1.7.',
  panelConnected: (s: number | null) =>
    s === null ? 'Panell connectat' : `Panell connectat · darrera crida fa ${s} s`,
  panelUnreachable: 'El panell no és accessible.',
  panelNotConfigured: 'Cap panell configurat en aquesta instal·lació.',
  panelRefused: 'El panell ha rebutjat les credencials de la plataforma.',
  running: 'en marxa',
  stopped: 'aturat',
  players: 'Jugadors',
  mission: 'Missió',
  mods: 'Mods',
  port: 'Port',
  start: 'Arrenca',
  stop: 'Atura',
  restart: 'Reinicia',
  refresh: 'Actualitza',
  noServers: 'El panell no té cap servidor.',
  backHome: 'Inici',
  budgetLine: (running: number, max: number) =>
    `${running} de ${max} servidors gestionats en marxa`,
  budgetRoom: (left: number) =>
    left > 0
      ? `Se’n poden engegar ${left} més alhora.`
      : 'Ja n’hi ha tants en marxa com permet el pressupost: el pots crear, però no engegar fins que se n’aturi un.',
  newServer: 'Nou servidor',
  configure: 'Configura',
  change: 'Canvia',
  remove: 'Esborra',
  link: 'Enllaça',
  unlink: 'Desenllaça',
  cancel: 'Cancel·la',
  save: 'Desa',
  create: 'Crea el servidor',
  access: {
    MANAGED: 'gestionat',
    LINKED: 'enllaçat del panell',
    NONE: 'del panell · només lectura',
  } as const,
  accessHint: {
    MANAGED: 'Creat des de la plataforma: es pot configurar, arrencar, aturar i esborrar.',
    LINKED: 'Servidor de l’operador: només arrencar, aturar i reiniciar.',
    NONE: 'Servidor de l’operador. Un administrador l’ha d’enllaçar per poder-lo operar.',
  } as const,
  serverName: 'Nom',
  serverDescription: 'Descripció',
  maxPlayers: 'Jugadors màxims',
  serverPassword: 'Contrasenya del servidor',
  battlEye: 'BattlEye',
  headlessClients: 'Clients headless',
  missionTemplate: 'Missió inicial (opcional)',
  lifecycle: 'Cicle de vida',
  startBefore: 'Arrenca (minuts abans de la sessió)',
  stopIdle: 'Atura (minuts sense ningú)',
  keepForCampaign: 'Conserva per a la campanya',
  retentionDays: 'Esborra passats (dies)',
  neverDelete: 'No l’esborris mai',
  dlcs: 'DLC que carrega',
  dlcsHint:
    'Cada DLC marcat el necessiten també tots els jugadors. Sense cap de marcat, el servidor no en carrega cap.',
  portAssigned: 'El port s’assigna en crear-lo.',
  deleteConfirm: (name: string) => `Per esborrar «${name}», escriu-ne el nom:`,
  admin: 'Administració',
  accounts: 'Comptes',
  newAccount: 'Nou compte',
  firstPassword: 'Primera contrasenya',
  firstPasswordHint:
    'Digues-la a la persona; podrà canviar-la quan tingui una altra manera d’entrar.',
  disabled: 'desactivat',
  disable: 'Desactiva',
  enable: 'Activa',
  activity: 'Activitat',
  lastSeen: 'Vist per últim cop',
  never: 'mai',
  catalog: 'Catàleg',
  catalogIntro: 'Què pot oferir cada servidor amb els mods que porta.',
  catalogIntroMore:
    'Terrenys, faccions, grups, vehicles, equipament. La lectura arrenca un servidor temporal amb els mateixos mods durant uns minuts; el servidor triat no es toca.',
  scanContent: 'Escaneja el contingut',
  scanning: (r: number, e: number | null) => `Escanejant… ${r}${e ? ` de ${e}` : ''} fragments`,
  scanned: 'Escanejat',
  notScanned: 'Encara no escanejat',
  scanFailed: 'L’escaneig ha fallat',
  terrains: 'Terrenys',
  scanTerrains: 'Indexa els terrenys',
  terrainsIntro: 'Llegits de les capçaleres dels PBO del servidor i dels mods del Workshop.',
  factions: 'Faccions',
  deriveFactions: 'Deriva els perfils',
  loadouts: 'Equipaments',
  loadoutPaste: 'Enganxa aquí el resultat de getUnitLoadout player',
  loadoutValidate: 'Comprova',
  loadoutSave: 'Desa l’equipament',
  loadoutMissing: (n: number) => `${n} classes no són en aquest servidor`,
  loadoutOk: 'El servidor porta totes les classes',
  loadoutName: 'Nom de l’equipament',
  loadoutImport: 'Importa d’una missió o d’una sessió',
  loadoutImportMission: 'Carpeta de missió',
  loadoutImportSession: 'Sessió jugada',
  loadoutImportNone: '— cap —',
  loadoutImportGo: 'Busca equipaments',
  loadoutImportEmpty: 'No s’hi ha trobat cap equipament.',
  loadoutImportEdit: 'Edita',
  loadoutImportSave: 'Desa',
  loadoutImportPlayers: (n: number) => (n === 1 ? '1 jugador' : `${n} jugadors`),
  modsPick: 'Mods del panell',
  missingMods: (n: number) =>
    n === 1 ? '1 mod encara no és al panell' : `${n} mods encara no són al panell`,
  installMissing: 'Descarrega’ls al panell',
  allModsReady: 'Tots els mods són al panell',
  newMission: 'Nova missió',
  step1: 'Pas 1 · Servidor',
  step2: 'Pas 2 · Mapa',
  step3: 'Pas 3 · Enemic',
  step4: 'Pas 4 · Què sabem de l’enemic',
  step5: 'Pas 5 · Tipus de missió',
  step6: 'Pas 6 · Les nostres forces',
  step7: 'Pas 7 · Qui decideix què',
  pickTerrain: 'Mapa',
  pickEnemy: 'Enemic',
  next: 'Següent',
  back: 'Enrere',
  sideNames: {
    west: 'BLUFOR',
    east: 'OPFOR',
    resistance: 'Independents',
    civilian: 'Civils',
  } as Record<string, string>,
  missionType: 'Tipus de missió',
  playersCount: 'Jugadors previstos',
  timeOfDay: 'Hora',
  timeSeeded: 'Aleatori',
  weather: 'Temps',
  /** When a night is set (ops.platform.setting.Era). */
  eras: {
    modern: 'Moderna',
    coldwar: 'Guerra Freda',
    vietnam: 'Vietnam',
    ww2: 'Segona Guerra Mundial',
  } as Record<string, string>,
  weatherNames: {
    clear: 'Clar',
    cloudy: 'Ennuvolat',
    rain: 'Pluja',
    fog: 'Boira',
    storm: 'Tempesta',
  } as Record<string, string>,
  whoDecides: 'Qui decideix què',
  decidedBy: {
    creator: 'El creador',
    voteBefore: 'Votació abans',
    voteDuring: 'Votació durant',
    chance: 'Aleatori',
    commander: 'El comandant',
    base: 'A la base, pel mateix mitjà',
  } as Record<string, string>,
  insertion: 'Inserció',
  insertionOptions: 'Opcions d’inserció',
  startFrom: {
    title: 'D’on surt la nit',
    land: 'De terra: una base de campanya',
    sea: 'Del mar: un grup amfibi',
    hint: 'Del mar, la base és la coberta del vaixell i els jugadors hi troben helicòpters per baixar a terra.',
    noCoast: 'Aquest terreny no té costa inspeccionada, així que només es pot sortir de terra.',
  },
  decisionsHint:
    'Les votacions «abans» s’obren en crear la sessió i es tanquen quan comença; les «durant» s’obren dins la missió, quan es resol l’objectiu triat, i es tanquen soles al cap d’uns minuts.',
  decisionKeys: {
    insertion: 'Inserció',
    extraction: 'Extracció',
    secondaryObjective: 'Objectiu secundari',
    time: 'Hora del dia',
    weather: 'Temps',
    twist: 'Gir',
    commander: 'Comandant',
    exfil: 'Sortida',
    supportsExtra: 'Suport extra',
  } as Record<string, string>,
  decidedByLabel: 'Qui ho decideix',
  opensAtObjective: 'S’obre quan es resol',
  atStart: 'En començar la missió',
  closesAfterMinutes: 'Es tanca al cap de (minuts)',
  optionsCommaSeparated: 'Opcions, separades per comes',
  removeDecision: 'Treu la decisió',
  addDecision: 'Afegeix una decisió',
  secondaryObjective: 'Objectiu secundari',
  sessionMods: {
    title: 'Mods de la partida',
    count: (mods: number, dlcs: number) =>
      `${mods === 1 ? '1 mod' : `${mods} mods`}${dlcs > 0 ? ` i ${dlcs} DLC` : ''}`,
    download: 'Baixa el preset per al Launcher (.html)',
    hint: 'Arrossega el fitxer a la finestra del Launcher de l’Arma 3 (o MODS › PRESET › IMPORT) i els mods es seleccionen sols; els que no tinguis es posen a baixar.',
  },
  supportsWhich: 'Suports que hi haurà',
  supportsNoCountHint:
    'Sense límit de crides: entre crida i crida hi ha un temps de recàrrega, al servidor. Com es demanen ho diu la pàgina «Objectius i suports» del mapa, dins el joc.',
  supportNames: {
    artillery: 'Artilleria',
    cas: 'Suport aeri',
    transport: 'Transport',
    supplyDrop: 'Llançament de subministraments',
    uav: 'UAV',
    medevac: 'Evacuació mèdica',
  } as Record<string, string>,
  objectives: 'Objectius',
  hiddenObjective: '? (ocult)',
  silentObjective: 'reacció enemiga, sense marcar ni llistar',
  priorityNames: { main: 'principal', secondary: 'secundari', hidden: 'ocult' } as Record<
    string,
    string
  >,
  cast: 'Repartiment',
  edit: 'Edita',
  run: 'Executa-la',
  runOn: 'Servidor',
  runNow: 'Crea la sessió',
  sessions: 'Sessions',
  sessionStates: { SCHEDULED: 'preparada', LIVE: 'en joc', ENDED: 'acabada' } as Record<
    string,
    string
  >,
  visibility: 'Visibilitat',
  visibilityNames: { PRIVATE: 'Privada', UNLISTED: 'Amb enllaç', PUBLIC: 'Pública' } as Record<
    string,
    string
  >,
  fork: 'Fes-ne una còpia',
  library: 'Missions',
  libraryEmpty: 'Cap missió encara. Crea la primera.',
  deleteMission: 'Esborra la missió',
  deleteMissionConfirm: (title: string) => ({
    title: `Esborrar «${title}»?`,
    body: 'Sortirà de la biblioteca i del servidor. Les partides jugades i els seus informes es mantenen, i la pots recuperar des de la paperera.',
  }),
  missionDeleted: 'Missió esborrada.',
  designer: 'Dissenyador',
  designerHint:
    'Fes clic al mapa per moure l’objectiu seleccionat; afegeix-ne amb el tipus de sota.',
  addObjective: 'Afegeix objectiu',
  removeObjective: 'Treu',
  objectiveTitle: 'Títol',
  objectiveType: 'Tipus',
  objectivePriority: 'Prioritat',
  objectiveRadius: 'Radi (m)',
  summary: 'Situació',
  saveNote: 'Nota del canvi',
  saved: 'Desat',
  nothingToSave: (n: number) => `Res per desar: tot és a la revisió ${n}`,
  folderBehind: (published: number, current: number) =>
    `La carpeta del servidor és de la revisió ${published} i la missió va per la ${current}: s’actualitzarà en arrencar la sessió.`,
  history: 'Historial',
  revision: (n: number) => `revisió ${n}`,
  briefing: 'Briefing',
  briefingRead: 'He llegit el briefing',
  kit: 'Equipament per rol',
  execution: 'Execució',
  phaseInsertion: (means: string | null) =>
    means ? `Inserció (${means.toLowerCase()})` : 'Inserció',
  phaseExtraction: (means: string | null) =>
    means ? `Extracció (${means.toLowerCase()})` : 'Extracció',
  phaseObjectives: (titles: string[]) => titles.join(' · '),
  phasesDerived: 'Les fases surten del graf d’objectius: cada una espera les de què depèn.',
  kitDefault: 'Equipament per defecte de la classe',
  kitCount: (n: number) => `×${n}`,
  briefingReads: (n: number) =>
    n === 1 ? '1 persona ha llegit el briefing' : `${n} persones han llegit el briefing`,
  undo: 'Desfés',
  withdrawn: 'retirada',
  delivered: 'ja al servidor',
  qr: 'QR',
  qrLabel: 'Codi QR de l’enllaç',
  radioNets: 'Xarxes de ràdio',
  session: 'Sessió',
  startSession: 'Arrenca la sessió',
  chooseServerToStart: 'Servidor on arrencar-la',
  sessionServer: 'Servidor de la sessió',
  sessionServerHint: 'Es pot canviar fins que la sessió arrenqui.',
  chooseServerOption: 'Tria un servidor…',
  endSession: 'Acaba la sessió',
  endSessionAsk: 'Acabar la sessió?',
  endSessionBody:
    'El resultat quedarà registrat, el servidor s’aturarà d’aquí a uns minuts i ningú més s’hi podrà unir des d’aquí.',
  restartSession: 'Torna a arrencar la sessió',
  timeline: 'Línia de temps',
  report: 'Informe de la partida',
  noEventsYet: 'Encara no hi ha cap esdeveniment. El servidor informa quan la missió arrenca.',
  eventKinds: {
    runtimeStarted: 'Missió en marxa',
    health: 'Estat del servidor',
    objectiveState: 'Objectiu',
    casualty: 'Baixa',
    supportUsed: 'Suport',
    commandAck: 'Ordre rebuda',
    snapshot: 'Instantània',
    snapshotWritten: 'Instantània',
    missionEnded: 'Missió acabada',
    restartLoop: 'Bucle de reinici al servidor',
    selfCheck: 'Autoexamen del runtime',
    placementReport: 'Informe de col·locació',
    scenePlacement: 'Informe de col·locació d’una escena',
    placementPlan: 'Pla de col·locació',
    ordersTaken: 'Ordres agafades',
    serverDown: 'Servidor aturat',
    log: 'Nota',
    script: 'Guió',
  } as Record<string, string>,
  direct: 'Direcció',
  profile: 'Perfil',
  profiles: 'Membres',
  myProfile: 'El meu perfil',
  joined: (d: string) => `Membre des de ${d}`,
  made: 'Ha fet',
  coEdited: 'Coedita',
  directed: 'Ha dirigit',
  decisions: (b: number) => (b === 1 ? '1 vot emès' : `${b} vots emesos`),
  profileStandings: 'Campanyes jugades',
  steamNudge: 'Enllaça Steam per veure les teves estadístiques de campanya aquí.',
  displayNameLabel: 'Nom visible',
  saveProfile: 'Desa el perfil',
  historyTab: 'Historial',
  nobodyListed: 'Cap membre visible.',
  derivedFrom: 'Derivada de',
  by: (h: string) => `de ${h}`,
  copyLink: 'Copia l’enllaç',
  linkCopied: 'Enllaç copiat',
  rotateCode: 'Canvia el codi de la sessió',
  rotateCodeHint: 'Si l’enllaç s’ha filtrat: només abans de començar.',
  presence: 'Editant ara',
  presenceAlone: 'Només tu',
  remoteChange: (h: string) => `${h} ha canviat la missió; s’ha actualitzat.`,
  staleRevision: 'Algú altre ha desat abans. S’ha recarregat; torna a aplicar el teu canvi.',
  liveOff: 'Sense canal en viu: es comprova cada 10 s.',
  libraryFilters: {
    all: 'Totes',
    mine: 'Meves',
    shared: 'Compartides amb mi',
    public: 'Públiques',
  } as Record<string, string>,
  search: 'Cerca',
  discordTest: 'Envia una prova a cada canal',
  discordChannels: { sessions: 'Sessions', votes: 'Votacions', changelog: 'Novetats' } as Record<
    string,
    string
  >,
  catalogScans: 'Escaneigs del catàleg',
  noScans: 'Cap lectura encara. Es llancen des del catàleg, servidor per servidor.',
  scanStates: {
    STARTING: 'arrencant',
    RUNNING: 'en curs',
    DONE: 'fet',
    FAILED: 'ha fallat',
  } as Record<string, string>,
  rescan: 'Torna a escanejar',
  openCatalog: 'Obre el catàleg',
  discordOk: 'Discord ha rebut la prova.',
  discordUnset: 'Sense sortida: ni canal del bot ni webhook.',
  discordFailed: 'Discord ha rebutjat la prova.',
  campaigns: 'Campanyes',
  campaign: 'Campanya',
  newCampaign: 'Nova campanya',
  campaignTitle: 'Nom de la campanya',
  campaignPlayers: 'Jugadors previstos per capítol',
  nextDecidedBy: 'Qui tria el capítol següent',
  nextDecidedByNames: {
    creator: 'El creador',
    vote: 'Una votació al final de cada capítol',
  } as Record<string, string>,
  gearPersistence: 'Persistència d’equip',
  gearAllow: 'Prefixos de classe permesos (un per línia; buit = sense persistència)',
  createCampaign: 'Crea la campanya',
  campaignBoard: 'Tauler de campanya',
  chapter: (n: number) => `Capítol ${n}`,
  chapters: 'Capítols',
  noChapters: 'Cap capítol encara. Genera el primer.',
  nextChapter: 'Genera el capítol següent',
  nextChapterRecipe: 'Tipus del capítol següent',
  fromState: 'El que demana el front',
  chapterPending: 'per jugar',
  day: (n: number) => `Dia ${n}`,
  enemyStrength: 'Força enemiga',
  civilianDisposition: 'Civils',
  civilianNames: (v: number) => (v > 0.3 ? 'a favor' : v < -0.3 ? 'en contra' : 'neutrals'),
  sectors: 'Sectors',
  sectorControl: { enemy: 'enemic', contested: 'disputat', friendly: 'nostre' } as Record<
    string,
    string
  >,
  frontLine: 'Línia del front',
  castMemory: 'Repartiment',
  castStatus: { alive: 'viu', dead: 'mort', captured: 'capturat', escaped: 'fugit' } as Record<
    string,
    string
  >,
  threads: 'Fils oberts',
  noThreads: 'Cap fil obert.',
  standings: 'Classificació',
  noStandings: 'Encara ningú ha jugat cap capítol.',
  attended: 'assistències',
  survived: 'supervivències',
  medals: 'Medalles',
  ranks: 'Rangs',
  nextChapterVoteOpen: 'La votació del capítol següent és oberta: ves a «Decideix».',
  finishCampaign: 'Acaba la campanya',
  finished: 'Acabada',
  finalReport: 'Informe final',
  verdicts: {
    won: 'Campanya guanyada',
    held: 'Front mantingut',
    lost: 'Campanya perduda',
  } as Record<string, string>,
  copyCampaign: 'Fes-ne una còpia',
  campaignDeltas: 'Què ha canviat a la campanya',
  nextVote: 'Votació del capítol següent',
  sectorsChanged: 'Sectors',
  medalsAwarded: 'Medalles',
  libraryTabs: { missions: 'Missions', campaigns: 'Campanyes' } as Record<string, string>,
  campaignsEmpty: 'Cap campanya encara.',
  /** The notice on the library's «Campanyes» tab until campaigns are played for real (roadmap R5). */
  campaignsSoon: {
    title: 'Les campanyes encara no estan disponibles.',
    text: 'Apareixeran aquí quan ho estiguin.',
  },
  tabs: { state: 'Estat', decide: 'Decidir', ask: 'Preguntar' } as Record<string, string>,
  votes: 'Votacions',
  noVotes: 'Cap votació oberta.',
  voteClosed: 'tancada',
  voteResult: 'Resultat',
  voteYours: 'el teu vot',
  voteClose: 'Tanca la votació',
  newVote: 'Nova votació',
  voteKey: 'Què decideix',
  voteQuestion: 'Pregunta',
  voteOptions: 'Opcions (una per línia)',
  voteClosesIn: 'Tanca sola al cap de (minuts, buit = a mà)',
  openVote: 'Obre la votació',
  direction: 'Direcció de la sessió',
  health: 'Salut del servidor',
  healthNone: 'Sense dades del servidor encara.',
  reveal: 'Revela',
  supportsAdd: 'Suports',
  supportsAddHint:
    'Cap suport té un nombre de crides: el que el reté és la recàrrega. «Disponible ara» la treu al menú del joc; un servei de Simplex porta la seva pròpia i no es pot tocar.',
  supportReadyNow: 'Disponible ara',
  reinforcements: 'Reforços',
  reinforcementsTo: 'Cap a l’objectiu',
  reinforcementsSend: 'Envia reforços',
  radio: 'Ràdio',
  radioFrom: 'Emissor',
  radioText: 'Missatge',
  radioSend: 'Emet',
  scriptedEvents: 'Esdeveniments',
  eventNames: {
    enemy_patrol: 'Patrulla enemiga a prop dels jugadors',
    civilian_unrest: 'Aldarulls civils a un objectiu',
    weather: 'Canvia el temps',
  } as Record<string, string>,
  fire: 'Dispara',
  snapshot: 'Fes una instantània',
  endMission: 'Acaba la missió',
  outcome: 'Resultat',
  // How a night ended. `partial` is the third end the runtime reaches by itself
  // (ENGINE_DESIGN.md §2.10, step E9b); `failure` is what the direction page
  // sent before it sent the runtime's own `failed`, kept for the sessions it named.
  outcomes: {
    success: 'Èxit',
    partial: 'Parcialment complerta',
    failed: 'Fracàs',
    ended: 'Acabada',
    failure: 'Fracàs',
    // A test card's session the platform ended because nobody was in it any more.
    empty: 'Tancada sola, sense cap jugador',
    // A test card's session the platform ended because its server had no player on it for a quarter of an hour.
    idle: 'Tancada sola, el servidor feia estona buit',
    // A test card's session, ended because the card was finished, closed or cancelled.
    test_done: 'Tancada en acabar la prova',
  } as Record<string, string>,
  /** The ends the direction page offers, in the order it offers them: the runtime's own words. */
  endOutcomes: ['success', 'partial', 'failed', 'ended'],
  serverChat: 'Xat del servidor (RCon)',
  serverSay: 'Envia al xat',
  serverChatText: 'Text per al xat del servidor',
  serverSwitch: 'Carrega la missió al servidor',
  commandSent: 'Ordre enviada',
  commandQueue: 'Ordres',
  /**
   * The server is refused to a start because people are on it (the panel's status gives how many,
   * not who: a headless client is not counted).
   */
  serverHasPlayersCount: (n: number) =>
    n === 1
      ? 'Hi ha 1 jugador connectat al servidor: espera que surti o atura’l abans d’arrencar-hi la sessió.'
      : `Hi ha ${n} jugadors connectats al servidor: espera que surtin o atura’l abans d’arrencar-hi la sessió.`,
  errors: {
    invalid_credentials: 'Usuari o contrasenya incorrectes.',
    throttled: 'Massa intents. Espera un quart d’hora i torna-ho a provar.',
    registration_closed: 'Cal un codi d’invitació vàlid per crear un compte.',
    handle_taken: 'Aquest nom d’usuari ja existeix.',
    no_proof:
      'La confirmació ha caducat o ja s’ha fet servir. Torna a entrar amb Steam o Discord i prova-ho de nou.',
    membership_unavailable:
      'Discord no ens ha respost i no hem pogut comprovar que siguis al servidor del grup. Torna-ho a provar d’aquí a una estona.',
    invalid_handle: 'El nom d’usuari no és vàlid.',
    invalid_password: 'La contrasenya és massa curta.',
    wrong_password: 'La contrasenya actual no és correcta.',
    invalid_displayName: 'El nom és massa llarg.',
    feature_off: 'Aquesta funció no està activada en aquesta instal·lació.',
    last_identity: 'No pots treure l’única manera d’entrar que tens.',
    identity_taken: 'Aquest identificador ja està enllaçat a un altre compte.',
    invalid_steamId:
      'No sembla un SteamID64: calen disset xifres, o l’adreça /profiles/… del perfil de Steam.',
    invalid_discordName:
      'No sembla un nom d’usuari de Discord: de 2 a 32 lletres minúscules, xifres, punt o guió baix.',
    invalid_accountId: 'Tria un compte.',
    panel_unreachable: 'El panell no és accessible.',
    panel_declined: 'El panell ho ha refusat.',
    preflight_unplaceable: 'Hi ha alguna cosa que la plataforma no pot situar enlloc.',
    server_not_linked:
      'Aquest servidor no està enllaçat a la plataforma: enllaça’l des de Servidors o tria’n un altre.',
    server_has_players:
      'El servidor té jugadors a dins: espera que surtin o atura’l abans d’arrencar la sessió.',
    start_in_progress:
      'Aquesta sessió ja s’està engegant. Espera que acabi abans de tornar-la a arrencar.',
    server_still_running:
      'El servidor no s’ha aturat a temps per carregar la sessió. Torna-ho a provar d’aquí a un moment.',
    server_did_not_start:
      'El servidor no ha arrencat. Mira’l al panell: pot ser que li faltin mods o que el port estigui ocupat.',
    panel_not_configured: 'Cap panell configurat en aquesta instal·lació.',
    panel_refused: 'El panell ha rebutjat les credencials de la plataforma.',
    http_403: 'No tens permís per fer això.',
    server_not_managed:
      'La plataforma no pot operar aquest servidor. Un administrador l’ha d’enllaçar.',
    no_server: 'La sessió no té cap servidor assignat.',
    rcon_failed: 'El servidor no ha respost a l’ordre RCon.',
    stale_revision: 'Algú altre ha desat abans. S’ha recarregat; torna a aplicar el teu canvi.',
    profile_not_found: 'Aquest perfil no existeix.',
    campaign_not_found: 'Aquesta campanya no existeix.',
    budget_exhausted:
      'Ja hi ha tants servidors gestionats en marxa com permet el pressupost. Atura’n un per engegar-ne un altre.',
    ports_exhausted:
      'No queda cap port lliure per a un servidor nou. Esborra’n un que ja no facis servir.',
    invalid_name: 'El nom no és vàlid.',
    invalid_launcherPreset:
      'Aquest fitxer no és un preset del Launcher o no porta cap mod del Workshop.',
    server_not_scanned:
      'Els mods d’aquest servidor han canviat o no s’han llegit mai: cal llegir-ne el contingut abans de fer-hi una missió.',
    mods_downloading:
      'El panell no tenia tots els mods i els ha posat a descarregar. Torna-ho a provar quan acabin.',
    last_admin: 'No es pot treure l’últim compte d’administració.',
    account_not_found: 'Aquest compte no existeix.',
    account_ambiguous:
      'Hi ha més d’un compte amb aquest nom: tria’l de la llista o escriu-ne el nom d’usuari.',
    invalid_role: 'Aquest rol no es pot concedir així.',
    mission_not_found: 'Aquesta missió no existeix.',
    mission_forbidden: 'No tens accés a aquesta missió.',
    mission_has_open_session:
      'Aquesta missió té una sessió en marxa o programada. Acaba-la o cancel·la-la abans d’esborrar-la.',
    invalid_definition: 'La definició no és vàlida.',
    session_not_found: 'Aquesta sessió no està disponible.',
    session_is_live: 'Aquesta sessió s’està jugant. Acaba-la abans d’esborrar-la.',
    not_possible:
      'No es pot fer ara: el servidor ja ho té, o cal el volum del panell muntat i un servidor.',
    probe_unavailable:
      'No es pot escanejar: cal el volum del panell muntat i el mod @ops instal·lat.',
    invalid_request: 'La petició no és vàlida.',
    incoherent_insertion: 'Del mar només s’hi pot entrar en helicòpter o en barca.',
    no_coast: 'Aquest mapa no té cap costa inspeccionada: només s’hi pot sortir de terra.',
    no_sea_in_reach:
      'Des d’aquest objectiu no hi ha mar a l’abast per a un vaixell (entre tres i quinze quilòmetres, lluny de la costa i de la vora del mapa). Tria un objectiu més a prop de la costa o surt de terra.',
    no_level_base:
      'A prop d’aquest objectiu no hi ha cap terreny on es pugui muntar una base, ni tan sols un campament mínim en terreny irregular, a la distància que permet aquesta entrada: o no és prou pla o, als mapes que tenen els camins inspeccionats, hi passa un camí o una carretera pel mig, i una base no es munta mai damunt d’un camí.',
    unknown_world:
      'Aquest mapa no existeix o no s’hi poden fer missions: Virtual Reality és el món buit de proves del joc.',
    unknown_decision:
      'Una de les decisions no és cap de les cinc maneres de decidir: el creador, votació abans, votació durant, atzar o el comandant.',
    same_side:
      'La nostra facció i l’enemic són del mateix bàndol, i al joc dues faccions d’un mateix bàndol són aliades: tria’n una d’un altre bàndol.',
    no_airfield:
      'Aquest mapa no té cap pista des d’on enlairar-se, o la que té és massa a prop de l’objectiu: no s’hi pot entrar en paracaigudes.',
    no_aircraft:
      'La nostra facció no té cap avió de transport per saltar-ne, ni les seves faccions germanes.',
    no_drop_zone:
      'No hi ha cap terreny obert i pla a prop de l’objectiu per fer-hi de zona de salt.',
    no_beach_in_reach:
      'No hi ha cap platja amb terreny sec i pla al darrere, i mar obert al davant, a una distància raonable d’aquest objectiu. Tria un altre objectiu o surt de terra.',
    no_landing_beach:
      'Un desembarcament no té sentit aquí: no hi ha cap platja on arribin les barcasses a menys de tres quilòmetres de l’objectiu. Tria un objectiu prop de la costa o surt de terra.',
    no_vehicle_for_insertion:
      'Per sortir del mar en helicòpter o en barca cal tenir-ne un a la base: afegeix-lo al pas «Sortida».',
    enemy_without_men:
      'L’enemic triat no té cap soldat a peu al catàleg d’aquest conjunt de mods, i sense soldats no es pot fer una nit contra ell. Tria una altra facció enemiga.',
    no_place_of_kind:
      'Aquest mapa no té el lloc que aquest tipus de missió necessita. Tria un altre mapa o un altre tipus de missió.',
    no_camp_ground:
      'Aquest mapa no té cap lloc que compleixi les regles del campament d’aquesta missió (una instal·lació militar, la vora d’un poble o terreny obert net). Tria un altre mapa.',
    no_camp_clear_ground:
      'Aquest mapa no té cap instal·lació militar per al campament enemic, i a la vora dels pobles no hi ha cap lloc pla, lluny dels camins i de les carreteres, on muntar-ne un de tendes: un campament no es munta mai damunt d’un camí. Tria un altre mapa.',
    no_radar_clear_ground:
      'Aquest mapa té altures amb accés per carretera, però en cap no hi ha lloc per a l’estació de radar sense que hi passi un camí o una carretera pel mig: una estació no es munta mai damunt d’un camí. Tria un altre mapa.',
    no_park_camp_clear_ground:
      'Al costat d’aquest parc de vehicles no hi ha cap lloc sec, lluny dels camins i de les carreteres, on muntar el campament dels conductors: un campament no es munta mai damunt d’un camí. Torna-ho a provar o tria un altre mapa.',
    no_workshop:
      'Aquest mapa no té cap poble habitat, a un o dos quilòmetres de la carretera de la ruta, amb una casa petita on posar el taller de qui col·loca els artefactes. Desmarca «Captura qui els col·loca» o tria un altre mapa.',
    no_park_yard_clear_ground:
      'Al costat d’aquest parc de vehicles no hi ha cap lloc sec, lluny dels camins i de les carreteres i sense edificis del mapa al mig, on muntar el pati del taller i de l’oficina: un pati no es munta mai damunt d’un camí ni travessant un edifici. Torna-ho a provar o tria un altre mapa.',
    no_scene_clear_ground:
      'Una de les instal·lacions d’aquesta missió (un campament, un post o una estació) no té cap lloc net on muntar-la: a tot el seu voltant hi passa un camí o una carretera, hi ha aigua o hi ha edificis del mapa al mig, i mai no es munta travessant un edifici ni damunt d’un camí. Torna-ho a provar o tria un altre mapa.',
    no_village:
      'Aquest mapa no té cap poble habitat prou gran on fer aquesta missió: només hi ha llogarets, o cap lloc habitat. Tria un altre mapa o un altre tipus de missió.',
    no_dry_objective:
      'Aquest mapa no té cap lloc amb nom ni terra seca dins del mapa on posar l’objectiu. Tria un altre mapa.',
    off_the_terrain:
      'Aquesta missió quedaria fora del mapa o dins l’aigua: aquest mapa no la pot contenir. Tria un altre mapa.',
    no_dry_extraction:
      'No hi ha terra seca, a la mateixa illa que l’objectiu, lluny de la vora del mapa i a un quilòmetre o més de l’objectiu, on posar el punt d’extracció. Tria un altre objectiu o torna a la base.',
    no_qrf_post:
      'Aquest mapa no té cap lloc a la banda oposada del campament, entre 1,2 i 6 quilòmetres per carretera, per al punt de reacció. Tria un altre mapa.',
    no_road_from_base:
      'El lliurament d’armes va per carretera i aquest mapa no té cap camí per carretera des de la base fins a la granja: ha de ser una base de terra amb una carretera que hi arribi. Tria un altre mapa o un altre punt de partida.',
    no_partisans:
      'El lliurament d’armes és per a una cèl·lula de partisans i el conjunt de mods no té cap facció irregular al nostre bàndol ni civils per armar: tria un altre conjunt de mods o un altre tipus de missió.',
    no_farmstead:
      'Aquest mapa no té cap granja aïllada ni cap clariana al bosc a la distància que cal per al lliurament. Tria un altre mapa.',
    recipe_coming_soon:
      'Aquest tipus de missió surt com a «Properament»: encara no està a punt i no es pot generar. Tria’n un dels que ja es poden jugar o dels que són «En proves».',
    recipe_not_generatable:
      'Aquest tipus de missió encara no es pot generar. Apareix al catàleg perquè es veu què vindrà, però encara no funciona.',
    no_route_destination:
      'Aquest mapa no té cap altre nucli on acabar la ruta. Tria un altre mapa o un altre tipus de missió.',
    no_route_clear_of_base:
      'Cap ruta per a la columna enemiga d’aquest mapa es manté a dos quilòmetres de la vostra base. Tria un altre mapa o un altre punt de partida.',
    no_watcher:
      'Per deixar la màquina cal el watcher: baixa’l aquí a «Proves», obre’l, marca-hi «es pot deixar» i torna a clicar «Ho faig».',
    session_ended: 'La sessió d’aquesta prova ja s’ha acabat.',
    already_lending:
      'Ja tens la màquina deixada per a una altra prova; quan aquella s’acabi, podràs agafar aquesta.',
    machine_locked:
      'La teva màquina està bloquejada (la pantalla d’inici de sessió de Windows): desbloqueja-la i torna-ho a provar d’aquí a un minut.',
    dlc_missing:
      'La teva màquina no té el DLC que necessita el mapa (o el conjunt de mods) d’aquesta prova: el joc no hi podria entrar.',
    dlc_unknown:
      'Aquesta prova necessita un DLC i el watcher encara no ha dit quins en té la teva màquina: obre’l (s’actualitza sol), marca-hi «es pot deixar» i torna-ho a provar d’aquí a un minut.',
    test_changed: 'La prova ha canviat mentrestant. Torna-ho a provar.',
    test_not_found: 'Aquesta prova no existeix, o no la pots veure.',
    not_the_tester:
      'Primer agafa la prova amb «Ho faig»: només qui l’ha agafada, o qui ha jugat la seva partida, hi pot contestar.',
    already_claimed: 'Aquesta prova ja l’ha agafada una altra persona.',
    not_open: 'Aquesta prova ja no està oberta.',
    steps_unanswered: 'Encara hi ha passos per contestar.',
    not_yours_to_stop:
      'El servidor d’una prova l’atura qui l’ha agafada, qui l’ha escrita o un administrador.',
    no_own_session: 'Aquesta prova no té cap partida pròpia per aturar.',
    server_not_up: 'El servidor d’aquesta prova ja està aturat.',
    shared_session:
      'Aquesta partida la comparteix una altra prova que encara està oberta: el servidor no s’atura fins que aquella s’acabi.',
    not_editable: 'Aquesta prova ja està revisada i tancada: ja no s’hi pot canviar res.',
    uploads_off: 'Aquí encara no es poden pujar fitxers; escriu-ho al comentari.',
    no_account: 'Per agafar una prova cal que entris amb el teu compte.',
    no_mission: 'Aquesta prova encara no té cap missió assignada.',
    server_busy: 'El servidor ja té una altra partida en marxa.',
    lend_needs_an_inspection_session:
      'Una màquina deixada només pot entrar en una sessió d’inspecció, que la col·loca sola al seu lloc.',
    param_not_admitted: 'Aquest tipus de missió no admet un dels valors triats.',
    twist_not_admitted: 'Aquest tipus de missió no admet un dels girs triats.',
    secondary_not_admitted: 'Aquest tipus de missió no admet un dels objectius secundaris triats.',
    unknown: 'Alguna cosa ha fallat. Torna-ho a provar.',
    /** The request never reached the platform (`OfflineError`): nothing was sent, nothing is kept to send later. */
    offline:
      'Sense connexió: no s’ha pogut fer, i res no queda pendent d’enviar. Torna-ho a provar quan tornis a tenir connexió.',
  } as Record<string, string>,
};

export type Strings = typeof ca;
